import React from 'react';
import { Box } from '@mui/material';

import AnalyticsWorkspaceBody from './AnalyticsWorkspaceBody';
import AnalyticsWorkspaceView from './AnalyticsWorkspaceView';
import WorkspaceChartTableView from './WorkspaceChartTableView';
import DashboardAccessGate from '../DashboardAccessGate';
import WorkspaceSinglePanelView from './WorkspaceSinglePanelView';
import {
  resolveWorkspacePanel,
  resolveWorkspaceViewLayout,
} from './workspaceDescriptorModel';
import {
  WORKSPACE_PANEL_KINDS,
} from './workspaceRegistry';

const PANEL_KIND_TO_SKELETON_TYPE = {
  [WORKSPACE_PANEL_KINDS.CHART]: 'chart',
  [WORKSPACE_PANEL_KINDS.TABLE]: 'table',
};

const getPanelKind = (panel = {}, model = {}) => (
  model.kind || panel.kind || WORKSPACE_PANEL_KINDS.TABLE
);

const buildPanelProps = ({
  viewDescriptor,
  panelIdOrKind,
  model,
}) => {
  const panel = resolveWorkspacePanel(viewDescriptor, panelIdOrKind) || {};
  const kind = getPanelKind(panel, model);

  return {
    ...model,
    kind,
    skeletonSpec: model.skeletonSpec || panel.skeleton || viewDescriptor?.skeleton,
    skeletonType: model.skeletonType || PANEL_KIND_TO_SKELETON_TYPE[kind] || kind,
  };
};

const renderPanelContent = (panel) => (
  typeof panel.render === 'function' ? panel.render(panel) : panel.children
);

function WorkspaceDescriptorPanels({
  viewDescriptor,
  panelModels = {},
}) {
  const panels = Array.isArray(viewDescriptor?.panels) ? viewDescriptor.panels : [];
  const layout = resolveWorkspaceViewLayout(viewDescriptor);

  if (layout === WORKSPACE_PANEL_KINDS.SPLIT || layout === WORKSPACE_PANEL_KINDS.CHART_TABLE) {
    const chartDescriptor = panels.find((panel) => panel.kind === WORKSPACE_PANEL_KINDS.CHART) || panels[0];
    const tableDescriptor = panels.find((panel) => panel.kind === WORKSPACE_PANEL_KINDS.TABLE) || panels[1];
    const chartModel = panelModels[chartDescriptor?.id] || panelModels.chart || {};
    const tableModel = panelModels[tableDescriptor?.id] || panelModels.table || {};
    const chart = buildPanelProps({ viewDescriptor, panelIdOrKind: chartDescriptor?.id || WORKSPACE_PANEL_KINDS.CHART, model: chartModel });
    const table = buildPanelProps({ viewDescriptor, panelIdOrKind: tableDescriptor?.id || WORKSPACE_PANEL_KINDS.TABLE, model: tableModel });

    return (
      <WorkspaceChartTableView
        chart={{ ...chart, children: renderPanelContent(chart) }}
        table={{ ...table, children: renderPanelContent(table) }}
        ratio={viewDescriptor?.ratio || panelModels.ratio}
        sizing={viewDescriptor?.sizing || panelModels.sizing}
      />
    );
  }

  if (panels.length <= 1) {
    const descriptor = panels[0] || {};
    const model = panelModels[descriptor.id] || panelModels[descriptor.kind] || panelModels.panel || {};
    const panel = buildPanelProps({ viewDescriptor, panelIdOrKind: descriptor.id || descriptor.kind, model });

    return (
      <WorkspaceSinglePanelView kind={panel.kind} panel={panel}>
        {renderPanelContent(panel)}
      </WorkspaceSinglePanelView>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {panels.map((descriptor) => {
        const model = panelModels[descriptor.id] || panelModels[descriptor.kind] || {};
        const panel = buildPanelProps({ viewDescriptor, panelIdOrKind: descriptor.id, model });

        return (
          <WorkspaceSinglePanelView key={descriptor.id} kind={panel.kind} panel={panel}>
            {renderPanelContent(panel)}
          </WorkspaceSinglePanelView>
        );
      })}
    </Box>
  );
}

function WorkspaceViewOutlet({
  children,
  viewDescriptor,
  panelModels,
  bodyDensity = 'compact',
  bodyScroll = 'panel',
  bodySx,
  viewLayout,
  viewSx,
  gate,
}) {
  const layout = viewLayout || resolveWorkspaceViewLayout(viewDescriptor);
  const content = children || (
    <WorkspaceDescriptorPanels
      viewDescriptor={viewDescriptor}
      panelModels={panelModels}
    />
  );

  const body = (
    <AnalyticsWorkspaceBody enabled density={bodyDensity} scroll={bodyScroll} sx={bodySx}>
      <AnalyticsWorkspaceView enabled layout={layout} sx={viewSx}>
        {content}
      </AnalyticsWorkspaceView>
    </AnalyticsWorkspaceBody>
  );

  if (!gate) return body;

  return (
    <DashboardAccessGate {...gate}>
      {body}
    </DashboardAccessGate>
  );
}

export default WorkspaceViewOutlet;
